import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';
import routes from 'sitewide-js/core_pre_webpack/routes';

import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { Body } from 'js/design_system/Body';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { fsEvent } from 'js/fullstory';
import {
  getProfileBulkDownloadFileList,
  getProfileBulkDownloadPreview,
  getBulkDownloadServerSideZipUrl,
  ProfilePreviewPaper,
  ProfilePreviewResponse,
} from 'js/bulk_download/bulk_download_helper';
import { BundleWork } from 'js/bulk_download/work_cards_shared/types';
import {
  PaperPreviewList,
  GradientOverlay,
} from 'js/bulk_download/work_cards_shared/PaperPreviewList';

function toBundleWork(paper: ProfilePreviewPaper): BundleWork {
  return {
    id: paper.id,
    title: paper.title,
    authors: paper.authors,
    thumbnailUrl: paper.thumbnailUrl ?? '',
    viewCount: paper.viewCount,
    bucket: paper.bucket,
    year: paper.year ?? undefined,
    relevancy: paper.relevancy ?? undefined,
  };
}

function formatViewCount(views: number): string {
  if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M`;
  if (views >= 1_000) return `${(views / 1_000).toFixed(0)}k`;
  return String(views);
}

function PackIncludes({
  sameAuthorCount,
  relatedPapersCount,
}: {
  sameAuthorCount: number;
  relatedPapersCount: number;
}) {
  return (
    <PackIncludesContainer>
      <Body size="xs" bold as="p" className="pack-label">
        Included in your PDF pack:
      </Body>
      <CheckList>
        <CheckItem>
          <Icon name="check" size={20} />
          <Body size="xs" as="span">
            All {sameAuthorCount} {sameAuthorCount === 1 ? 'paper' : 'papers'}{' '}
            by this author
          </Body>
        </CheckItem>
        {relatedPapersCount > 0 && (
          <CheckItem>
            <Icon name="check" size={20} />
            <Body size="xs" as="span">
              {relatedPapersCount}{' '}
              {relatedPapersCount === 1 ? 'paper' : 'papers'} citing this
              author&apos;s work
            </Body>
          </CheckItem>
        )}
        <CheckItem>
          <Icon name="check" size={20} />
          <Body size="xs" as="span">
            Related research in this field
          </Body>
        </CheckItem>
      </CheckList>
    </PackIncludesContainer>
  );
}

interface Props {
  viewedUserId: number;
  viewedUserDisplayName: string;
  worksCount?: number;
  isPremium?: boolean;
}

export const ProfilePdfPackCard: React.FC<Props> = ({
  viewedUserId,
  viewedUserDisplayName,
  worksCount = 0,
  isPremium = false,
}) => {
  const [preview, setPreview] = useState<ProfilePreviewResponse | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (worksCount === 0) return;
    getProfileBulkDownloadPreview(viewedUserId)
      .then(setPreview)
      .catch(() => {});
  }, [viewedUserId, worksCount]);

  const handlePremiumDownload = async () => {
    fsEvent('ProfilePdfPack.CTAClicked', {
      viewedUserId_int: viewedUserId,
      isPremium_bool: true,
    });
    setIsDownloading(true);
    setError(null);

    try {
      const data = await getProfileBulkDownloadFileList(viewedUserId);
      const validFiles = (data.files || []).filter((f) => f.url && f.filename);

      if (validFiles.length === 0) {
        setError('No downloadable papers found for this author.');
        return;
      }

      const zipUrl = await getBulkDownloadServerSideZipUrl(
        validFiles,
        `${viewedUserDisplayName}_papers`,
        data.zipperUrl,
      );

      if (!zipUrl) {
        setError('Failed to create download package. Please try again.');
        return;
      }

      const link = document.createElement('a');
      link.href = zipUrl;
      link.download = `${viewedUserDisplayName}_papers.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFreeUpgrade = () => {
    fsEvent('ProfilePdfPack.CTAClicked', {
      viewedUserId_int: viewedUserId,
      isPremium_bool: false,
    });
    window.location.href = routes.checkout_url({
      feature: 'BULK_DOWNLOAD',
      trigger: 'profile-pdf-pack',
    });
  };

  if (worksCount === 0) return null;

  const title = isPremium
    ? `Download a curated PDF Pack of ${viewedUserDisplayName}\u2019s research`
    : `Download ${viewedUserDisplayName}\u2019s most-viewed papers and top related research`;

  const sameAuthorCount = preview?.sameAuthorCount ?? 0;
  const relatedPapersCount = preview?.relatedPapersCount ?? 0;
  const totalViews = preview?.totalViews ?? 0;
  const papers = preview?.papers ?? [];
  const bundleWorks = papers.map(toBundleWork);

  const statParts: string[] = [];
  if (sameAuthorCount > 0) {
    statParts.push(
      `${sameAuthorCount} ${sameAuthorCount === 1 ? 'paper' : 'papers'}`,
    );
  }
  if (totalViews > 0) {
    statParts.push(`${formatViewCount(totalViews)} views`);
  }

  return (
    <ImpressionWatcher
      source="profile_pdf_pack_card"
      entity_type={ImpressionEntity.User}
      entity_id={viewedUserId}
      extra={{ is_premium: isPremium }}
    >
      <OuterCard>
        <HeaderSection>
          <Body size="lg" bold as="h3">
            {title}
          </Body>
          {statParts.length > 0 && (
            <StatsLine>
              {statParts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <MetaSep>|</MetaSep>}
                  <Body size="xs" as="span">
                    {part}
                  </Body>
                </React.Fragment>
              ))}
            </StatsLine>
          )}
        </HeaderSection>

        <Divider />

        <InnerCard>
          <PaperPreviewList
            works={bundleWorks}
            maxHeight="280px"
            footer={!isPremium ? <GradientOverlay /> : undefined}
          />

          <BottomSection>
            <PackIncludes
              sameAuthorCount={sameAuthorCount}
              relatedPapersCount={relatedPapersCount}
            />

            {error && (
              <ErrorMessage>
                <Body size="sm" as="p">
                  {error}
                </Body>
              </ErrorMessage>
            )}

            <CtaContainer>
              <ImpressionWatcher
                source="profile_pdf_pack_cta"
                entity_type={ImpressionEntity.User}
                entity_id={viewedUserId}
                extra={{ is_premium: isPremium }}
              >
                {isPremium ? (
                  <Button
                    icon="download"
                    fullWidth
                    onClick={handlePremiumDownload}
                    disabled={isDownloading}
                    trackClick={{
                      source: 'profile_pdf_pack_cta_click',
                      entityId: viewedUserId,
                      extra: { is_premium: true },
                    }}
                  >
                    {isDownloading
                      ? 'Preparing Download...'
                      : 'Download PDF Pack'}
                  </Button>
                ) : (
                  <FreeCtaButton
                    onClick={handleFreeUpgrade}
                    trackClick={{
                      source: 'profile_pdf_pack_cta_click',
                      entityId: viewedUserId,
                      extra: { is_premium: false },
                    }}
                  >
                    <Body size="md" bold>
                      Try Premium for $1
                    </Body>
                    <CtaSubline>
                      <Body size="sm" as="span">
                        And Download your PDF Pack
                      </Body>
                      <Icon name="arrow_forward" size={16} />
                    </CtaSubline>
                  </FreeCtaButton>
                )}
              </ImpressionWatcher>
            </CtaContainer>
          </BottomSection>
        </InnerCard>
      </OuterCard>
    </ImpressionWatcher>
  );
};

const OuterCard = styled.div`
  background: var(--background-light-blue, #eef2f9);
  border-radius: var(--corner-radius-radius-lg, 16px);
  padding: var(--spacing-xs-16, 16px);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
`;

const StatsLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  color: var(--text-secondary, #535366);
`;

const MetaSep = styled(Body).attrs({ size: 'xs', as: 'span' })`
  color: var(--text-secondary, #535366);
`;

const Divider = styled.div`
  height: 1px;
  background: var(--background-grey, #dddde2);
`;

const InnerCard = styled.div`
  background: var(--background-white, white);
  border-radius: var(--corner-radius-radius-md, 8px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  align-items: center;
  padding: var(--spacing-xs-16, 16px);
`;

const PackIncludesContainer = styled.div`
  @import 'stylesheets/design_system/_breakpoint.scss';
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
  align-items: flex-start;
  width: 100%;

  @include breakpoint(medium, large) {
    align-items: center;
  }

  .pack-label {
    color: var(--text-primary, #141413);

    @include breakpoint(medium, large) {
      text-align: center;
    }
  }
`;

const CheckList = styled.div`
  @import 'stylesheets/design_system/_breakpoint.scss';
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-8, 8px);

  @include breakpoint(medium, large) {
    justify-content: center;
  }
`;

const CheckItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-4, 4px);
  color: var(--text-primary, #141413);
`;

const CtaContainer = styled.div`
  width: 100%;
  max-width: 349px;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
  align-items: center;
`;

const FreeCtaButton = styled(Button)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 67px;
`;

const CtaSubline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErrorMessage = styled.div`
  color: var(--text-danger, #c42929);
  text-align: center;
`;
